<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import type { FormElement, HeaderProperties, Spacing } from '../../../model/form.types';
  import { useFormBuilderStore } from '../../../model/form-builder.store';
  import { useBranchLogo } from '@/entities/Branch';
  import { DEFAULT_HEADER_MAIN_LABEL, DEFAULT_HEADER_MARGIN } from '../../../lib/element-defaults';
  import { isKeyboardEventFromEditableTarget } from '../../../lib/isKeyboardEventFromEditableTarget';

  interface Props {
    element: FormElement & { properties: HeaderProperties };
    pageIndex?: number;
    onSelect?: (pageIndex: number) => void;
    applyMargin?: boolean;
    readonly?: boolean;
    lockRqField?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    pageIndex: 0,
    applyMargin: true,
    readonly: false,
    lockRqField: false,
  });

  const RQ_LOCKED_HINT = 'O RQ de um Registro já enviado para aprovação não pode ser alterado';

  const formBuilderStore = useFormBuilderStore();
  const { currentLogoUrl, handleLogoError } = useBranchLogo();

  const margin = computed((): Spacing => {
    const byPage = props.element.properties.marginByPage;
    const page = byPage?.[props.pageIndex];
    return page ? { ...DEFAULT_HEADER_MARGIN, ...page } : { ...DEFAULT_HEADER_MARGIN };
  });

  const wrapperStyle = computed(() => {
    if (!props.applyMargin) return {};
    return {
      marginTop: `${margin.value.top}px`,
      marginRight: `${margin.value.right}px`,
      marginBottom: `${margin.value.bottom}px`,
      marginLeft: `${margin.value.left}px`,
    };
  });

  const mainLabel = ref(props.element.properties.mainLabel ?? DEFAULT_HEADER_MAIN_LABEL);
  const rq = ref(props.element.properties.rq ?? '');
  const rev = ref(props.element.properties.rev ?? '');
  const title = ref(props.element.properties.title ?? '');

  watch(
    () => [
      props.element.properties.mainLabel,
      props.element.properties.rq,
      props.element.properties.rev,
      props.element.properties.title,
    ],
    ([m, r, revVal, t]) => {
      mainLabel.value = (m as string) ?? DEFAULT_HEADER_MAIN_LABEL;
      rq.value = (r as string) ?? '';
      rev.value = (revVal as string) ?? '';
      title.value = (t as string) ?? '';
    },
    { immediate: true, flush: 'post' }
  );

  const RQ_REGEX = /^\d*(?:\.\d*)?$/;
  const REV_REGEX = /^\d*(?:\.\d*)?$/;

  const isRqLocked = computed(() => props.readonly || props.lockRqField);
  const rqHint = computed(() =>
    props.lockRqField && !props.readonly ? RQ_LOCKED_HINT : undefined
  );

  function updateMainLabel(value: string) {
    if (props.readonly) return;
    mainLabel.value = value;
    formBuilderStore.updateElement(props.element.id, {
      properties: { ...props.element.properties, mainLabel: value },
    } as Partial<FormElement>);
  }

  function updateRq(value: string) {
    if (isRqLocked.value) return;
    if (value !== '' && !RQ_REGEX.test(value)) return;
    rq.value = value;
    formBuilderStore.updateElement(props.element.id, {
      properties: { ...props.element.properties, rq: value },
    } as Partial<FormElement>);
  }

  function updateRev(value: string) {
    if (props.readonly) return;
    if (value !== '' && !REV_REGEX.test(value)) return;
    rev.value = value;
    formBuilderStore.updateElement(props.element.id, {
      properties: { ...props.element.properties, rev: value },
    } as Partial<FormElement>);
  }

  function updateTitle(value: string) {
    if (props.readonly) return;
    title.value = value;
    formBuilderStore.updateElement(props.element.id, {
      properties: { ...props.element.properties, title: value },
    } as Partial<FormElement>);
  }

  function onWrapperClick(e: MouseEvent) {
    e.stopPropagation();
    props.onSelect?.(props.pageIndex);
  }

  function onWrapperKeyDown(e: KeyboardEvent) {
    if (isKeyboardEventFromEditableTarget(e)) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      props.onSelect?.(props.pageIndex);
    }
  }
</script>

<template>
  <div
    class="form-header-block"
    :style="wrapperStyle"
    role="button"
    tabindex="0"
    @click="onWrapperClick"
    @keydown="onWrapperKeyDown"
  >
    <table
      class="form-header-table"
      cellspacing="0"
      cellpadding="0"
    >
      <colgroup>
        <col class="form-header-col-logo">
        <col class="form-header-col-main">
        <col class="form-header-col-fields">
      </colgroup>
      <tbody>
        <tr>
          <td
            rowspan="2"
            class="form-header-cell form-header-logo-cell"
          >
            <div class="form-header-logo">
              <div
                v-if="currentLogoUrl"
                class="form-header-logo-inner"
              >
                <img
                  :src="currentLogoUrl"
                  alt="Logo"
                  class="form-header-logo-img"
                  @error="handleLogoError"
                >
              </div>
            </div>
          </td>
          <td class="form-header-cell form-header-main-label-cell">
            <input
              :value="mainLabel"
              type="text"
              class="form-header-main-label-input"
              :disabled="readonly"
              @input="(e) => updateMainLabel((e.target as HTMLInputElement).value)"
            >
          </td>
          <td
            rowspan="1"
            class="form-header-cell form-header-rq-rev-cell"
          >
            <div class="form-header-rq-rev-row">
              <div class="form-header-field-wrap">
                <label
                  :for="`rq-${element.id}`"
                  class="form-header-field-label"
                >RQ</label>
                <input
                  :id="`rq-${element.id}`"
                  :value="rq"
                  type="text"
                  class="form-header-field-input"
                  placeholder="00.00"
                  :disabled="isRqLocked"
                  :title="rqHint"
                  @input="(e) => updateRq((e.target as HTMLInputElement).value)"
                >
              </div>
              <div class="form-header-field-wrap">
                <label
                  :for="`rev-${element.id}`"
                  class="form-header-field-label"
                >REV.</label>
                <input
                  :id="`rev-${element.id}`"
                  :value="rev"
                  type="text"
                  class="form-header-field-input"
                  placeholder="00"
                  :disabled="readonly"
                  @input="(e) => updateRev((e.target as HTMLInputElement).value)"
                >
              </div>
            </div>
          </td>
        </tr>
        <tr>
          <td
            colspan="2"
            class="form-header-cell form-header-title-cell"
          >
            <input
              :value="title"
              type="text"
              class="form-header-title-input"
              placeholder="Título do Registro"
              :disabled="readonly"
              @input="(e) => updateTitle((e.target as HTMLInputElement).value)"
            >
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
  .form-header-block {
    font-family: var(--font-base);
    background: var(--color-white-01);
    border: 1px solid var(--color-gray-02);
    border-radius: 0.375rem;
    cursor: pointer;
    outline: none;
  }

  .form-header-block:focus {
    outline: none;
  }

  .form-header-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  .form-header-cell {
    border: 1px solid var(--color-gray-02);
    vertical-align: middle;
    padding: 8px;
  }

  .form-header-col-logo {
    width: 18%;
  }

  .form-header-col-main {
    width: 32%;
  }

  .form-header-col-fields {
    width: 50%;
  }

  .form-header-logo-cell {
    border-right: 1px solid var(--color-gray-02);
  }

  .form-header-logo {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 100%;
    padding: 8px;
    box-sizing: border-box;
    background: var(--color-white-01);
  }

  .form-header-logo-inner {
    width: 100%;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 0;
  }

  .form-header-logo-img {
    width: 100%;
    height: 100%;
    max-height: 56px;
    object-fit: contain;
  }

  .form-header-rq-rev-cell {
    width: 50%;
  }

  .form-header-rq-rev-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    width: 100%;
  }

  .form-header-field-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
  }

  .form-header-main-label-cell {
    text-align: center;
  }

  .form-header-main-label-input {
    width: 100%;
    background: transparent;
    border: none;
    font-size: 14px;
    font-weight: 700;
    color: var(--color-gray-07);
    text-align: center;
    outline: none;
    text-transform: uppercase;
  }

  .form-header-main-label-input::placeholder {
    color: #9ca3af;
  }

  .form-header-main-label-input:disabled,
  .form-header-field-input:disabled,
  .form-header-title-input:disabled {
    opacity: 1;
    cursor: default;
  }

  .form-header-field-label {
    font-size: 12px;
    font-weight: 700;
    color: var(--color-gray-07);
    flex-shrink: 0;
  }

  .form-header-field-input {
    flex: 1;
    min-width: 0;
    height: 32px;
    padding: 4px 8px;
    border: 1px solid var(--color-gray-02);
    border-radius: 6px;
    font-size: 12px;
    background: var(--color-white-01);
    color: var(--color-gray-07);
  }

  .form-header-field-input::placeholder {
    color: #9ca3af;
  }

  .form-header-title-cell {
    border-top: 1px solid var(--color-gray-02);
  }

  .form-header-title-input {
    width: 100%;
    height: 32px;
    padding: 4px 8px;
    border: 1px solid var(--color-gray-02);
    border-radius: 6px;
    font-size: 12px;
    background: var(--color-white-01);
    color: var(--color-gray-07);
  }

  .form-header-title-input::placeholder {
    color: #9ca3af;
  }
</style>
